import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { JobPosting } from '../types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { CLOSE_REASON_PLATFORMS, CLOSE_REASON_OTHERS } from '../constants/closeReasons';

type Stage = '100' | '180';

const stageOf = (posting: JobPosting): Stage | null => {
  if (posting.hireCheck180AskedAt && posting.hireCheck180Response == null) return '180';
  if (posting.hireCheck100AskedAt && posting.hireCheck100Response == null) return '100';
  return null;
};

// 登入後彈窗詢問僱主：貼文上線滿 100/180 天，是否已招到外傭
export const HireCheckPrompt: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [queue, setQueue] = useState<JobPosting[]>([]);
  const [showReasonPicker, setShowReasonPicker] = useState(false);
  const [closeReasons, setCloseReasons] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== 'EMPLOYER') return;
    apiService.jobPostings.getPendingHireChecks().then((r: any) => {
      if (r.success && r.data?.length) setQueue(r.data);
    }).catch(() => {});
  }, [user]);

  const current = queue[0] ?? null;
  const stage = current ? stageOf(current) : null;

  const advance = useCallback(() => {
    setQueue(prev => prev.slice(1));
    setShowReasonPicker(false);
    setCloseReasons(new Set());
  }, []);

  const handleFoundHelper = () => setShowReasonPicker(true);

  const submitFoundHelper = async () => {
    if (!current || !stage) return;
    setSubmitting(true);
    try {
      const reasonStr = Array.from(closeReasons).join(',');
      await apiService.jobPostings.answerHireCheck(current.id, stage, true);
      await apiService.jobPostings.updateStatus(current.id, 'CLOSED', reasonStr || undefined);
      advance();
    } finally {
      setSubmitting(false);
    }
  };

  const handleNotYet = async () => {
    if (!current || !stage) return;
    setSubmitting(true);
    try {
      await apiService.jobPostings.answerHireCheck(current.id, stage, false);
      if (stage === '180') {
        setNotice(t('hireCheck.offlineNotice', { title: current.headline || current.title }));
      }
      advance();
    } finally {
      setSubmitting(false);
    }
  };

  const toggleReason = (code: string) => {
    setCloseReasons(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  if (notice) {
    return (
      <Dialog open onOpenChange={() => setNotice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('hireCheck.offlineTitle', '招聘帖子已下架')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{notice}</p>
          <DialogFooter>
            <Button onClick={() => setNotice(null)}>{t('hireCheck.gotIt', '知道了')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!current || !stage) return null;

  if (showReasonPicker) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) setShowReasonPicker(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('hireCheck.closeJobTitle', '下架職位')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{t('hireCheck.chooseReason', '請選擇下架原因（可多選）')}</p>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('hireCheck.channelQuestion', '透過哪個渠道找到外傭？')}</p>
              {CLOSE_REASON_PLATFORMS.map(r => (
                <label key={r.code} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-muted/40">
                  <Checkbox checked={closeReasons.has(r.code)} onCheckedChange={() => toggleReason(r.code)} />
                  <span className="text-sm">{t(r.labelKey, r.label)}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReasonPicker(false)}>{t('common.back', '返回')}</Button>
            <Button variant="destructive" onClick={submitFoundHelper} disabled={submitting}>
              {submitting ? t('hireCheck.processing', '處理中...') : t('hireCheck.confirmClose', '確認下架')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('hireCheck.foundHelperTitle', '您已招到外傭了嗎？')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground py-2">
          {t('hireCheck.foundHelperBody', { title: current.headline || current.title, stage })}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={handleNotYet} disabled={submitting}>{t('hireCheck.notYet', '還沒有')}</Button>
          <Button onClick={handleFoundHelper} disabled={submitting}>{t('hireCheck.yesFound', '是，已招到')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HireCheckPrompt;
